/**
 * Отказ «нет живой сессии». Вынесен константой, потому что его РАЗБИРАЕТ клиент:
 * получив его, он чистит токен и уходит на экран входа (см. `lib/mobile/api.ts`).
 *
 * Раньше эта строка жила в двух местах текстом. Стоило переписать формулировку
 * на сервере — и клиент переставал узнавать истёкшую сессию, продолжая
 * показывать ошибку на каждой кнопке вместо экрана входа. Молча: ни тесты, ни
 * типы такого не ловят. Модуль без серверных зависимостей, поэтому его
 * импортируют обе стороны.
 *
 * ⚠️ Меняя текст — менять здесь, и только здесь.
 */
export const AUTH_REQUIRED = "Требуется авторизация";

/**
 * Отказ уволенному, отключённому, удалённому (код `auth_revoked`, 17.10, Б17):
 * диспетчер, продление и вход по паролю или PIN, если секрет подошёл к
 * закрытой учётке.
 */
export const AUTH_REVOKED = "Доступ закрыт: учётка отключена или удалена. Если это ошибка — обратитесь к руководителю";

/** Отказ смены PIN: текущий PIN или пароль не подошёл (код `pin_current_wrong`). */
export const PIN_CURRENT_WRONG = "Текущий PIN или пароль неверен";

/** Отказ смены PIN: PIN уже задан, а текущий не прислан (код `pin_current_required`). */
export const PIN_CURRENT_REQUIRED = "Введите текущий PIN или пароль";

/** Отказ: первый PIN не сразу после входа — подтвердите паролем (код `password_required`). */
export const PASSWORD_REQUIRED = "Подтвердите паролем";

/**
 * Машинный код отказа (12.10, задача iOS) — поле `code` рядом с `error`.
 * Текст `error` остаётся для человека и может меняться; клиент решает по
 * коду, а не по тексту (урок AUTH_REQUIRED выше). Кода нет — обычная
 * бизнес-ошибка: показать текст.
 *
 *  · auth_required    — нет живой сессии: продлить (`refreshSession`) или войти;
 *  · forbidden        — сессия есть, прав нет (роль, точка);
 *  · rate_limited     — слишком часто: подождать (срок — в тексте);
 *  · module_off       — модуль выключен у заведения: спрятать раздел;
 *  · tenant_mismatch  — пропуск от другого ресторана;
 *  · tenant_unknown   — ресторан не определён (нет `x-tenant` / такого нет);
 *  · update_required  — версия приложения ниже минимальной: экран «обновите»;
 *  · profile_required — сначала заполнить профиль (поля — в тексте);
 *  · refresh_race     — ключ только что обменян параллельным запросом: повторить;
 *  · owner_refused    — владельцу нельзя удалить учётку: в поддержку.
 *  · auth_revoked     — учётка уволена, отключена или удалена (17.10, Б17):
 *                       не продлевать и не спрашивать PIN — полный выход.
 *  · pin_current_required — смена PIN: PIN уже задан, пришлите `currentPin`
 *                       или `password` (Б24);
 *  · pin_current_wrong — смена PIN: текущий PIN или пароль не подошёл —
 *                       вернуться к шагу «текущий PIN».
 *  · password_required — первый PIN не сразу после входа: пришлите `password`.
 *  · device_signed_out — push не зарегистрирован: на этом устройстве нет
 *                       живого входа (выход уже был) — не повторять.
 *  · request_in_progress — запрос с этим Idempotency-Key ещё выполняется:
 *                       повторить с тем же ключом через несколько секунд;
 *  · idempotency_mismatch — ключ уже использован для запроса с другим телом:
 *                       ошибка клиента, нужен новый ключ.
 *  · test_time_expired — время на попытку теста вышло: попытка записана
 *                       проваленной, повтор отправки отвечает тем же;
 *  · push_uncertain   — отправка в iiko прервалась, исход неизвестен: показать
 *                       текст и повторить только с подтверждением человека
 *                       (`resendUncertain: true`);
 *  · method_not_allowed — изменение пришло не через POST (HTTP 405): ошибка клиента.
 *
 * ⚠️ Коды — контракт с приложением: добавлять можно, менять и удалять — нет.
 */
export const ERROR_CODES = [
  "auth_required",
  "forbidden",
  "rate_limited",
  "module_off",
  "tenant_mismatch",
  "tenant_unknown",
  "update_required",
  "profile_required",
  "refresh_race",
  "owner_refused",
  "auth_revoked",
  "pin_current_required",
  "pin_current_wrong",
  "password_required",
  "device_signed_out",
  "request_in_progress",
  "idempotency_mismatch",
  "push_uncertain",
  "test_time_expired",
  "method_not_allowed",
  // Человек уже на параллельной смене этой точки (бар + зал): сервер просит
  // подтверждения, клиент повторяет assignShift с confirmParallel.
  "parallel_shift",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

// Осознанная бизнес-ошибка: её message безопасно вернуть клиенту в конверте.
// Все прочие (неожиданные) исключения маскируются в dispatch и не светят детали.
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | undefined;

  constructor(message: string, status = 200, code?: ErrorCode) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}
